/**
 * Domain routing hook for custom domain-based agent chat routing
 * Detects if the current domain is attached to an agent and handles routing
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { publicApi } from '@/lib/api/public'
import { KNOWN_APP_DOMAINS as ENV_KNOWN_APP_DOMAINS } from '@/lib/env'
import { getAccessToken } from '@/utils/auth-storage'

/**
 * Default app domains that should skip domain lookup
 * These are the standard domains where the app is hosted
 */
const DEFAULT_APP_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'taqat.ai',
  'app.taqat.ai',
  'staging.taqat.ai',
  'tmmate.ai',
  'store.no-downtime.com'
]

/**
 * Combined list of known app domains (defaults + environment configured)
 */
const KNOWN_APP_DOMAINS = [...DEFAULT_APP_DOMAINS, ...ENV_KNOWN_APP_DOMAINS]

/**
 * Check if the current hostname is a known app domain
 * Exported for use in other modules (e.g., conditional-auth-provider)
 */
export function isKnownAppDomain(hostname: string): boolean {
  return KNOWN_APP_DOMAINS.some((domain) => hostname === domain)
}

export interface DomainRoutingState {
  isCheckingDomain: boolean
  domainAgentId: string | null
  domainTeamId: string | null
  isCustomDomain: boolean
  domainNotFound: boolean
}

/**
 * Hook to detect custom domain and redirect to agent chat if applicable
 * Returns loading state to show a loader while checking domain
 * Only runs ONCE on initial mount to prevent infinite loops
 */
export function useDomainRouting(): DomainRoutingState {
  const navigate = useNavigate()
  const [isCheckingDomain, setIsCheckingDomain] = useState(true)
  const [domainAgentId, setDomainAgentId] = useState<string | null>(null)
  const [domainTeamId, setDomainTeamId] = useState<string | null>(null)
  const [isCustomDomain, setIsCustomDomain] = useState(false)
  const [domainNotFound, setDomainNotFound] = useState(false)

  // Track if we've already done the lookup to prevent re-running
  const hasCheckedRef = useRef(false)

  useEffect(() => {
    // Only run once
    if (hasCheckedRef.current) {
      return
    }
    hasCheckedRef.current = true

    const hostname = window.location.hostname
    const pathname = window.location.pathname

    // Skip for known app domains - no loading needed
    if (isKnownAppDomain(hostname)) {
      setIsCheckingDomain(false)
      setIsCustomDomain(false)
      return
    }

    // This is a custom domain
    setIsCustomDomain(true)

    // Check if already on an agent-specific page (to determine if we need to redirect)
    const isOnAgentAuthPage = pathname.includes('/agent/') &&
      (pathname.includes('/login') || pathname.includes('/register'))
    const isOnAgentChatPage = pathname.includes('/agent/') && pathname.includes('/chat')
    // The SSO handoff page must be left alone: the lookup still runs (so the
    // custom-domain route block, which renders /signin/sso/callback, is active),
    // but we must NOT redirect away from it — it stores the session tokens from
    // the URL and then navigates on itself.
    const isOnSsoCallback = pathname.startsWith('/signin/sso/callback')
    const isOnTeamAuthPage = pathname.includes('/teams/') &&
      (pathname.includes('/login') || pathname.includes('/register'))
    const isOnTeamChatPage = pathname.includes('/teams/') && pathname.includes('/chat')
    const isOnAdminAuthPage = pathname === '/signin' || pathname === '/signup'

    // Always do domain lookup to get agentId/teamId (needed for route rendering)
    publicApi
      .lookupDomain(hostname)
      .then((result) => {
        if (result.found && result.agentId) {
          setDomainAgentId(result.agentId)

          // Skip redirect if already on correct agent pages or the SSO handoff
          if (isOnAgentAuthPage || isOnAgentChatPage || isOnSsoCallback) {
            return
          }

          // If on admin auth pages (/signin, /signup), redirect to agent login
          if (isOnAdminAuthPage) {
            navigate(`/agent/${result.agentId}/login`, { replace: true })
            return
          }

          // Otherwise, redirect based on auth status
          const token = getAccessToken()

          if (token) {
            navigate(`/agent/${result.agentId}/chat/new`, { replace: true })
          } else {
            navigate(`/agent/${result.agentId}/login`, { replace: true })
          }
        } else if (result.found && result.teamId) {
          setDomainTeamId(result.teamId)

          // Skip redirect if already on correct team pages or the SSO handoff
          if (isOnTeamAuthPage || isOnTeamChatPage || isOnSsoCallback) {
            return
          }

          // If on admin auth pages, redirect to team login
          if (isOnAdminAuthPage) {
            navigate(`/teams/${result.teamId}/login`, { replace: true })
            return
          }

          // Redirect based on auth status
          const token = getAccessToken()

          if (token) {
            navigate(`/teams/${result.teamId}/chat/new`, { replace: true })
          } else {
            navigate(`/teams/${result.teamId}/login`, { replace: true })
          }
        } else if (result.isAppDomain) {
          // This is a known app/admin domain - proceed as normal app (no 404)
          setIsCustomDomain(false)
        } else {
          // Domain not found or not published - show 404
          setDomainNotFound(true)
        }
      })
      .finally(() => {
        setIsCheckingDomain(false)
      })
  }, [navigate])

  return { isCheckingDomain, domainAgentId, domainTeamId, isCustomDomain, domainNotFound }
}
